import React from 'react';

/**
 * Orbe du dossier AO — lecture instantanée de l'état d'un appel d'offres.
 *
 * Un anneau segmenté : une part par lot, remplie à proportion de son
 * avancement et colorée par son état. Deux métriques (pièces validées / score
 * matching), sélection d'un lot, et un CTA qui ouvre l'assistant au bon endroit.
 *
 * Composant présentationnel : les lots arrivent calculés depuis DossiersAO.
 */

const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;

const R = 74, CX = 98, CIRC = 2 * Math.PI * R, GAP = 10;

// Barème identique au reste de l'application : 75 / 50.
export const orbeTone = (metric, lot) => {
  if (metric === 'score') {
    const v = lot.avg;
    if (v == null) return 'idle';
    return v >= 75 ? 'ok' : v >= 50 ? 'warn' : 'bad';
  }
  if (!lot.tot || lot.ok === 0) return 'bad';
  return lot.pct >= 100 ? 'ok' : lot.pct >= 50 ? 'warn' : 'bad';
};

const TONE = {
  ok: { ink: '#16a34a', chip: 'rgba(45,200,110,0.15)', label: 'Complet' },
  warn: { ink: '#d97706', chip: 'rgba(217,119,6,0.14)', label: 'En cours' },
  bad: { ink: '#cf3b32', chip: 'var(--danger-bg)', label: 'À traiter' },
  idle: { ink: 'var(--text-3)', chip: 'var(--chip-bg)', label: 'Non évalué' },
};

const mono = { fontFamily: "'IBM Plex Mono',monospace" };
const btn = { display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', borderRadius: '9px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text-2)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' };
const btnMain = { ...btn, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontWeight: 700 };

const initials = (n) => (n || '?').split(/[\s_-]+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
/** Référence courte : on retire le numéro de marché commun à tous les lots. */
const shortRef = (label) => (label || '').replace(/\s*\d{2}-\d{2}-\d{4}-\d{3}\s*$/, '').trim() || label;

export default function AoOrbe({ lots, metric, onMetric, selected, onSelect, onCta, matchingLoading }) {
  const n = lots.length;
  if (!n) return null;

  const slice = CIRC / n;
  const seg = Math.max(slice - GAP, 2);
  const valOf = (lot) => metric === 'score' ? (lot.avg ?? 0) : lot.pct;

  // Centre : la valeur globale du dossier selon la métrique affichée.
  const totPieces = lots.reduce((a, l) => a + l.tot, 0);
  const okPieces = lots.reduce((a, l) => a + l.ok, 0);
  const scored = lots.filter(l => l.avg != null);
  const globalVal = metric === 'score'
    ? (scored.length ? Math.round(scored.reduce((a, l) => a + l.avg, 0) / scored.length) : null)
    : (totPieces ? Math.round((okPieces / totPieces) * 100) : 0);
  const cands = lots.reduce((a, l) => a + l.cands.length, 0);
  const sel = lots.find(l => l.path === selected) || null;

  return (
    <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '18px', padding: '18px 20px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px' }}>
      <style>{`@keyframes orbeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}`}</style>

      {/* ── Anneau ── */}
      <div style={{ position: 'relative', width: '196px', height: '196px', flex: 'none' }}>
        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', pointerEvents: 'none', background: 'radial-gradient(circle at 50% 45%, rgba(255,255,255,0.06), rgba(0,0,0,0.04) 70%)' }} />
        <svg width="196" height="196" viewBox="0 0 196 196">
          <g transform={`rotate(-90 ${CX} ${CX})`}>
            {lots.map((lot, i) => {
              const off = -(i * slice);
              const tone = TONE[orbeTone(metric, lot)];
              const filled = Math.max(0, Math.min(100, valOf(lot))) / 100 * seg;
              const active = selected && lot.path === selected;
              return (
                <g key={lot.path}>
                  <circle cx={CX} cy={CX} r={R} fill="none" style={{
                    stroke: 'var(--hair)', strokeWidth: 17,
                    strokeDasharray: `${seg} ${CIRC - seg}`, strokeDashoffset: off,
                    opacity: selected && !active ? 0.35 : 1, transition: 'opacity .3s',
                  }} />
                  <circle cx={CX} cy={CX} r={R} fill="none" onClick={() => onSelect(active ? null : lot.path)} style={{
                    stroke: tone.ink, strokeWidth: 17, strokeLinecap: 'round', cursor: 'pointer',
                    strokeDasharray: `${filled} ${CIRC - filled}`, strokeDashoffset: off,
                    opacity: selected && !active ? 0.35 : 1,
                    filter: active ? `drop-shadow(0 0 7px ${tone.ink}88)` : 'none',
                    transition: 'stroke-dasharray .55s cubic-bezier(.22,.61,.36,1), stroke .3s, opacity .3s',
                  }}>
                    <title>{`${lot.label} — ${metric === 'score' ? (lot.avg != null ? `${lot.avg} pts` : 'non évalué') : `${lot.ok}/${lot.tot} pièces`}`}</title>
                  </circle>
                </g>
              );
            })}
          </g>
        </svg>
        {/* Le trou utile est étroit : on borne le carré intérieur pour que rien
            ne passe sous l'anneau. */}
        <div style={{ position: 'absolute', inset: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', pointerEvents: 'none' }}>
          <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '34px', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, color: 'var(--text-strong)' }}>
            {globalVal == null ? '—' : (metric === 'score' ? globalVal : `${globalVal}%`)}
          </div>
          <div style={{ marginTop: '5px', maxWidth: '104px', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)' }}>
            {metric === 'score' ? 'Score moyen' : 'Pièces validées'}
          </div>
          <div style={{ ...mono, marginTop: '3px', maxWidth: '104px', fontSize: '10.5px', color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {metric === 'score' ? `${n} lot${n > 1 ? 's' : ''}` : `${okPieces}/${totPieces}`}
          </div>
        </div>
      </div>

      {/* ── Colonne droite ── */}
      <div style={{ flex: 1, minWidth: '280px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '180px' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>Orbe du dossier</div>
            <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '2px' }}>
              {n} lot{n > 1 ? 's' : ''} · {cands} candidat{cands > 1 ? 's' : ''} · {okPieces}/{totPieces} pièces validées
            </div>
          </div>
          <div style={{ display: 'inline-flex', border: '1px solid var(--input-border)', borderRadius: '8px', overflow: 'hidden', flex: 'none' }}>
            {[['pieces', 'Pièces validées'], ['score', 'Score matching']].map(([id, lbl]) => (
              <button key={id} onClick={() => onMetric(id)}
                style={{ padding: '7px 12px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px', fontWeight: metric === id ? 600 : 500, background: metric === id ? 'var(--accent-soft-bg)' : 'var(--ghost-bg)', color: metric === id ? 'var(--text-strong)' : 'var(--text-2)' }}>
                {lbl}
              </button>
            ))}
          </div>
        </div>

        {metric === 'score' && matchingLoading && (
          <div style={{ marginTop: '9px', fontSize: '11.5px', color: 'var(--text-2)' }}>
            <BI icon="arrow-repeat" style={{ marginRight: '6px', animation: 'ao-spin .9s linear infinite' }} />Calcul des scores…
          </div>
        )}

        {/* Légende cliquable */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(226px, 1fr))', gap: '5px', marginTop: '11px' }}>
          {lots.map(lot => {
            const tone = TONE[orbeTone(metric, lot)];
            const active = selected === lot.path;
            return (
              <button key={lot.path} onClick={() => onSelect(active ? null : lot.path)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left', padding: '6px 8px', border: 'none', borderRadius: '7px', cursor: 'pointer', fontFamily: 'inherit', background: active ? 'var(--ghost-bg-hover, var(--ghost-bg))' : 'transparent' }}>
                <span style={{ width: '10px', height: '10px', flex: 'none', borderRadius: '3px', background: tone.ink }} />
                <span title={lot.label} style={{ flex: 1, minWidth: 0, fontSize: '12px', fontWeight: active ? 600 : 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shortRef(lot.label)}</span>
                <span style={{ ...mono, flex: 'none', fontSize: '10.5px', color: 'var(--text-2)' }}>
                  {metric === 'score' ? (lot.avg != null ? `${lot.avg} pts` : '—') : `${lot.ok}/${lot.tot}`}
                </span>
              </button>
            );
          })}
        </div>

        {/* Panneau du lot sélectionné */}
        {sel && (() => {
          const tone = TONE[orbeTone(metric, sel)];
          return (
            <div style={{ marginTop: '12px', padding: '12px 13px', border: '1px solid var(--input-border)', borderRadius: '11px', animation: 'orbeIn .3s cubic-bezier(.22,.61,.36,1) both' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
                <span style={{ flex: 1, minWidth: '160px', fontSize: '12.5px', fontWeight: 700, color: 'var(--text-strong)', wordBreak: 'break-word' }}>{sel.label}</span>
                <span style={{ flex: 'none', fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', background: tone.chip, color: tone.ink }}>{tone.label}</span>
                <button onClick={() => onCta(sel, metric)} style={{ ...btnMain, flex: 'none' }}>
                  {metric === 'score' ? 'Revue des CV' : 'Compléter ce lot'}
                </button>
              </div>
              <div style={{ fontSize: '11.5px', color: 'var(--text-2)', marginTop: '4px' }}>
                {metric === 'score'
                  ? (sel.avg != null ? `Score moyen ${sel.avg} sur ${sel.cands.length} candidat${sel.cands.length > 1 ? 's' : ''} — la Revue des CV les passe un par un.` : 'Aucun score calculé pour ce lot.')
                  : `${sel.ok}/${sel.tot} pièces validées — l'assistant part de la première pièce manquante.`}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(226px, 1fr))', gap: '7px', marginTop: '10px' }}>
                {sel.cands.map(c => {
                  const pct = c.tot ? Math.round((c.ok / c.tot) * 100) : 0;
                  const cTone = TONE[orbeTone(metric, { pct, ok: c.ok, tot: c.tot, avg: c.score })];
                  return (
                    <div key={c.nom} style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 8px', border: '1px solid var(--hair)', borderRadius: '9px', minWidth: 0 }}>
                      <span style={{ width: '26px', height: '26px', flex: 'none', borderRadius: '7px', background: 'var(--accent-soft-bg)', color: 'var(--text-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10.5px', fontWeight: 700 }}>{initials(c.nom)}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div title={c.nom} style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nom}</div>
                        <div style={{ height: '3px', margin: '4px 0 3px', borderRadius: '2px', background: 'var(--hair)', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: cTone.ink, transition: 'width .4s' }} />
                        </div>
                        <div style={{ ...mono, fontSize: '10px', color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.ok}/{c.tot} pièces{c.score != null ? ` · ${c.score} pts` : ''}
                        </div>
                      </div>
                      {c.score != null && (
                        <span style={{ ...mono, flex: 'none', fontSize: '11px', fontWeight: 700, color: TONE[c.score >= 75 ? 'ok' : c.score >= 50 ? 'warn' : 'bad'].ink }}>{c.score}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
